/* eslint-disable import/prefer-default-export */

import { number, string, oneOf, bool } from 'prop-types';
import cloudinary from 'util/cloudinary';
import cloudinaryFetch from 'util/cloudinary/cloudinaryFetch';

// allows for images IDs to be mapped to full image URLs
export const LEGACY_IMAGE_ID_PREFIX =
  'https://s3.amazonaws.com/pixtruder/original_images/';

// these image rendering options are service-agnostic.
//
// NOTE: some services treat keys differently, so we need to convert/transform
// the values when we map the render options in the individual services.
export const renderOpsPropTypes = {
  // simple mappings (for now); these basically work the same in all services
  format: string,
  quality: string,
  width: number,
  height: number,

  // complex mappings
  grayscale: bool, // true if grayscale
  colorize: number, // percent
  colorFill: string, // hex color
  filter: string, // filter name
  pixelate: number, // radius

  // we basically have two use cases: "crop" an image (used mostly in <picture>
  // elements), or "scaleFit" the entire image into a bounding box, which
  // results in an image which has one side that equals a provided dimension
  // while the other is smaller. the third use case 'stretch' is used
  // specifically for 'common' placeholder images (due to the way the images are
  // constructed -- a white square PNG that is stretched to fit).
  resizeMode: oneOf(['crop', 'scaleFit', 'stretch'])
};

// these options produce a chunky pixelated style
export const placeholderRenderOps = {
  format: 'png',
  quality: undefined, // quality doesn't affect PNG format
  pixelate: 100
};

// these options produce a single color image that is the same dimensions as the
// original.
export const placeholderRenderTransforms = [
  {
    grayscale: true,
    colorize: 100,
    colorFill: 'cccccc',
    format: 'png',
    filter: 'png8',
    quality: undefined // quality doesn't affect PNG format
  }
];

/**
 * returns a service-specific URL for provided canonical URL and renderOpts.
 *
 * this method has been simplified to just test the canonical image URL against
 * cloudinary's base testUrl() method and, if it matches, return cloudinary URL
 * with supplied renderOpts applied.
 *
 * if it does not match (it's an external URL), then return image URL prefixed
 * with the cloudinary fetch API URL, with renderOpts applied.
 *
 * note that renderOps can be an object or array of objects (to represent
 * multiple transform operations),
 *
 * @export
 * @param {String} url canonical image URL
 * @param {Object|Object[]} [renderOpts={}] options or an array of options (aka transforms)
 * @return {String} service-specific image URL
 */
export function imageUrl(url, renderOpts = {}) {
  // now that we're assuming cloudinary (upload) for all images, we can just
  // test cloudinary and fall back to cloudinaryFetch.
  return cloudinary.testUrl(url)
    ? cloudinary.createUrl(url, renderOpts)
    : cloudinaryFetch.createUrl(url, renderOpts);

  // original logic for multiple service providers:
  //
  // const serviceKey = Object.keys(IMAGE_SERVICES).find(s =>
  //   IMAGE_SERVICES[s].testUrl(url)
  // );
  //
  // return serviceKey
  //   ? IMAGE_SERVICES[serviceKey].createUrl(url, renderOpts)
  //   : url;
}

/**
 * maps 40 character ID to full image storage URL on S3.
 *
 * CLEANUP: in some places (algolia, for example) we only have image IDs, so we
 * need to handle creating an image URL from just that. this will eventually go
 * away and we'll have full image URLs available everywhere.
 *
 * @export
 * @param {String} id legacy image ID
 * @returns {String}
 */
export function mapIdToUrl(id) {
  return `${LEGACY_IMAGE_ID_PREFIX}${id}`;
}

/**
 * Checks if an image file path ends with the given extension.

 * @export
 * @param {String} path image file path
 * @param {String} extension file extension to look for (no dot)
 * @returns {Bool}
 */
export function hasFileExtension(path, extension) {
  const re = new RegExp(`\\.${extension}$`, 'i');
  return re.test(path);
}
